// Captura rápida: dictás una frase y queda la tarea archivada.
//
// El dictado lo hace el teclado de Android, no la app: sólo hace falta un
// <textarea> con autofocus y el micrófono queda a un toque. Nada de Web Speech
// API — el teclado anda mejor, no consume nada y ya está entrenado.

import { api } from "../api";
import { esc } from "../html";
import { ETIQUETA_TIPO_TAREA } from "../etiquetas";
import { TIPOS_TAREA, PRIORIDADES } from "../../lib/tipos";
import type { ProyectoListado, Tarea, TipoTarea } from "../../lib/tipos";
import { analizarCaptura } from "../../lib/captura";
import type { ProyectoConocido } from "../../lib/captura";
import { hoyMontevideo } from "../../lib/fecha";

export async function renderCapturar(contenedor: HTMLElement): Promise<void> {
  let proyectos: ProyectoListado[] = [];
  // Los chips que el usuario corrigió a mano dejan de pisarse con lo que
  // detecta el parser: si te tomaste el trabajo de arreglarlo, mandás vos.
  const tocados = new Set<string>();
  // "Agregar y seguir dictando" recuerda el proyecto: se dicta el código una
  // vez y después van tres o cuatro notas seguidas del mismo.
  let ultimoProyecto: number | null = null;

  const el = <T extends HTMLElement>(sel: string) => contenedor.querySelector(sel) as T;

  function conocidos(): ProyectoConocido[] {
    return proyectos.map((p) => ({
      id: p.id,
      codigo: p.codigo,
      alias: p.alias,
      nombre: p.nombre,
    }));
  }

  /** Relee el dictado y actualiza los chips que el usuario no tocó. */
  function analizar(): void {
    const texto = el<HTMLTextAreaElement>("#texto").value;
    const r = analizarCaptura(texto, conocidos(), hoyMontevideo());

    if (!tocados.has("proyecto")) {
      el<HTMLSelectElement>("#cProyecto").value = String(r.proyectoId ?? ultimoProyecto ?? "");
    }
    if (!tocados.has("tipo")) el<HTMLSelectElement>("#cTipo").value = r.tipo;
    if (!tocados.has("prioridad")) {
      el<HTMLSelectElement>("#cPrioridad").value = String(r.prioridad);
    }
    if (!tocados.has("fecha")) el<HTMLInputElement>("#cFecha").value = r.venceEl ?? "";

    // El aviso de confianza baja no bloquea nada: sólo avisa que mires el chip.
    const dudoso = texto.trim() !== "" && r.confianza === "BAJA" && !tocados.has("proyecto");
    el<HTMLElement>("#cProyecto").classList.toggle("ring-2", dudoso);
    el<HTMLElement>("#cProyecto").classList.toggle("ring-alerta", dudoso);
    el<HTMLElement>("#avisoProyecto").classList.toggle("hidden", !dudoso);

    el<HTMLElement>("#previo").textContent = r.titulo || "—";
  }

  // Freno al doble envio: mientras la tarea viaja al servidor no se manda otra.
  // Sin esto, tocar dos veces (o que el boton dispare dos eventos) daba de alta
  // la misma tarea dos veces.
  let enviando = false;

  /** Bloquea o suelta los dos botones y avisa que esta guardando. */
  function bloquear(si: boolean): void {
    const bAgregar = el<HTMLButtonElement>("#bAgregar");
    const bSeguir = el<HTMLButtonElement>("#bSeguir");
    bAgregar.disabled = si;
    bSeguir.disabled = si;
    bAgregar.textContent = si ? "GUARDANDO…" : "AGREGAR";
  }

  async function guardar(seguir: boolean): Promise<void> {
    if (enviando) return;
    const texto = el<HTMLTextAreaElement>("#texto").value.trim();
    const err = el<HTMLElement>("#err");
    err.textContent = "";
    if (!texto) return;

    const r = analizarCaptura(texto, conocidos(), hoyMontevideo());
    const proyecto = el<HTMLSelectElement>("#cProyecto").value;
    const fecha = el<HTMLInputElement>("#cFecha").value;

    enviando = true;
    bloquear(true);
    try {
      await api.post<Tarea>("/api/tareas", {
        proyecto_id: proyecto ? Number(proyecto) : null,
        // Si el parser dejó el título vacío (todo eran palabras consumidas),
        // se guarda el dictado entero: nunca una tarea sin texto.
        titulo: (r.titulo || texto).slice(0, 200),
        // La red de seguridad: el dictado original, siempre.
        detalle: texto,
        tipo: el<HTMLSelectElement>("#cTipo").value as TipoTarea,
        prioridad: Number(el<HTMLSelectElement>("#cPrioridad").value),
        vence_el: fecha || null,
        origen: "DICTADO",
      });
    } catch (e) {
      err.textContent = e instanceof Error ? e.message : "No se pudo guardar";
      return; // el finally suelta los botones y el texto queda para reintentar
    } finally {
      enviando = false;
      bloquear(false);
    }

    ultimoProyecto = proyecto ? Number(proyecto) : null;
    if (seguir) {
      const caja = el<HTMLTextAreaElement>("#texto");
      caja.value = "";
      tocados.clear();
      analizar();
      caja.focus(); // el teclado no se baja: se sigue dictando
      const ok = el<HTMLElement>("#ok");
      ok.textContent = "Guardada. Seguí dictando.";
      setTimeout(() => (ok.textContent = ""), 2500);
    } else {
      location.hash = ultimoProyecto ? `#/proyecto/${ultimoProyecto}` : "#/bandeja";
    }
  }

  try {
    proyectos = await api.get<ProyectoListado[]>("/api/proyectos");
  } catch (e) {
    contenedor.innerHTML = `<div class="tarjeta text-alerta">No se pudieron cargar los proyectos: ${esc(
      e instanceof Error ? e.message : "error",
    )}</div>`;
    return;
  }

  const conCodigo = proyectos.filter((p) => p.codigo);

  contenedor.innerHTML = `
    <h1 class="titulo-pagina">Captura rápida</h1>
    <p class="subtitulo">Dictá empezando por el código del proyecto.</p>

    <textarea id="texto" rows="4" maxlength="1000" autofocus
      class="campo mt-4 text-lg"
      placeholder="proyecto B mejorar la UX de la pantalla de pedidos"></textarea>

    <p class="mt-1 text-xs text-apagado">
      Se guarda como: <span id="previo" class="text-texto">—</span>
    </p>

    <div class="mt-3 grid grid-cols-2 gap-2">
      <label><span class="etiqueta">Proyecto</span>
        <select id="cProyecto" class="campo">
          <option value="">Bandeja de entrada</option>
          ${proyectos
            .map(
              (p) =>
                `<option value="${p.id}">${esc(p.nombre)}${p.codigo ? ` (${esc(p.codigo)})` : ""}</option>`,
            )
            .join("")}
        </select></label>
      <label><span class="etiqueta">Tipo</span>
        <select id="cTipo" class="campo">
          ${TIPOS_TAREA.map((t) => `<option value="${t}">${ETIQUETA_TIPO_TAREA[t]}</option>`).join("")}
        </select></label>
      <label><span class="etiqueta">Prioridad</span>
        <select id="cPrioridad" class="campo">
          ${PRIORIDADES.map((n) => `<option value="${n}">P${n}</option>`).join("")}
        </select></label>
      <label><span class="etiqueta">Vence</span>
        <input id="cFecha" type="date" class="campo" /></label>
    </div>

    <p id="avisoProyecto" class="mt-2 hidden text-xs text-alerta">
      No estoy seguro del proyecto. Miralo antes de guardar, o dejalo en la bandeja.
    </p>

    <p id="err" class="mt-2 text-sm text-alerta"></p>
    <p id="ok" class="mt-2 text-sm text-ok"></p>

    <button id="bAgregar" class="btn-primario mt-3 w-full py-4 text-lg">AGREGAR</button>
    <button id="bSeguir" class="btn-sec mt-2 w-full">Agregar y seguir dictando</button>

    ${
      conCodigo.length
        ? `<p class="mt-5 text-xs text-apagado">
             Códigos que reconozco:
             ${conCodigo.map((p) => `<b class="text-texto">${esc(p.codigo)}</b> ${esc(p.nombre)}`).join(" · ")}
           </p>`
        : `<div class="tarjeta mt-5 text-sm text-apagado">
             Todavía no le pusiste <b class="text-texto">código</b> a ningún proyecto, así que todo
             va a caer en la bandeja. Se los ponés desde la ficha de cada proyecto, en
             <i>Editar proyecto</i>.
           </div>`
    }`;

  el<HTMLTextAreaElement>("#texto").addEventListener("input", analizar);
  for (const [sel, campo] of [
    ["#cProyecto", "proyecto"],
    ["#cTipo", "tipo"],
    ["#cPrioridad", "prioridad"],
    ["#cFecha", "fecha"],
  ] as const) {
    el<HTMLElement>(sel).addEventListener("change", () => tocados.add(campo));
  }

  el<HTMLElement>("#bAgregar").addEventListener("click", () => void guardar(false));
  el<HTMLElement>("#bSeguir").addEventListener("click", () => void guardar(true));

  el<HTMLTextAreaElement>("#texto").focus();
  analizar();
}
